if (!window.BpdColorSwatchStripInitialized) {
  window.BpdColorSwatchStripInitialized = true;

  if (!customElements.get('bpd-color-swatch-scroll')) {
    customElements.define(
      'bpd-color-swatch-scroll',
      class BpdColorSwatchScroll extends HTMLElement {
        constructor() {
          super();

          this.handleScroll = this.handleScroll.bind(this);
          this.handleNextClick = this.handleNextClick.bind(this);
          this.handlePreviousClick = this.handlePreviousClick.bind(this);
          this.update = this.update.bind(this);
          this.initialized = false;
        }

        connectedCallback() {
          this.initialize();
        }

        initialize() {
          if (this.initialized) return;

          this.strip = this.querySelector('.bpd-color-swatch-strip');
          this.previousButton = this.querySelector('[data-bpd-color-swatch-previous]');
          this.nextButton = this.querySelector('[data-bpd-color-swatch-next]');
          if (!this.strip || !this.previousButton || !this.nextButton) return;

          this.reorderSwatches();
          this.initialized = true;
          this.strip.addEventListener('scroll', this.handleScroll, { passive: true });
          this.previousButton.addEventListener('click', this.handlePreviousClick);
          this.nextButton.addEventListener('click', this.handleNextClick);
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this.strip);

          this.restoreScrollPosition();
          this.update();
          this.classList.add('bpd-color-swatch-scroll--ready');
          requestAnimationFrame(this.update);
        }

        reorderSwatches() {
          const preferredOrder = (this.dataset.bpdColorOrder || '')
            .split(',')
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean);
          if (preferredOrder.length === 0) return;

          const preferredIndex = new Map(preferredOrder.map((value, index) => [value, index]));
          const swatches = Array.from(this.strip.querySelectorAll('input[type="radio"]'))
            .map((input, originalIndex) => ({
              input,
              label: input.nextElementSibling,
              originalIndex,
            }))
            .filter(({ label }) => label)
            .sort((left, right) => {
              const leftIndex = preferredIndex.get(left.input.value.trim().toLowerCase());
              const rightIndex = preferredIndex.get(right.input.value.trim().toLowerCase());
              const leftRank = leftIndex ?? preferredOrder.length + left.originalIndex;
              const rightRank = rightIndex ?? preferredOrder.length + right.originalIndex;
              return leftRank - rightRank;
            });

          swatches.forEach(({ input, label }) => this.strip.append(input, label));
        }

        disconnectedCallback() {
          this.strip?.removeEventListener('scroll', this.handleScroll);
          this.previousButton?.removeEventListener('click', this.handlePreviousClick);
          this.nextButton?.removeEventListener('click', this.handleNextClick);
          this.resizeObserver?.disconnect();
          this.initialized = false;
        }

        handleScroll() {
          if (this.hasPendingInteractionScroll()) {
            this.update();
            return;
          }

          this.persistScrollPosition();
          this.update();
        }

        handlePreviousClick() {
          this.strip.scrollBy({
            left: -Math.max(this.strip.clientWidth * 0.65, 120),
            behavior: 'smooth',
          });
        }

        handleNextClick() {
          this.strip.scrollBy({
            left: Math.max(this.strip.clientWidth * 0.65, 120),
            behavior: 'smooth',
          });
        }

        restoreScrollPosition() {
          const pendingInteractionScrollLeft = this.readPendingInteractionScrollLeft();
          const scrollLeft = Number.isFinite(pendingInteractionScrollLeft)
            ? pendingInteractionScrollLeft
            : Number.isFinite(window.BpdColorSwatchScrollLeft)
              ? window.BpdColorSwatchScrollLeft
              : this.shouldRestoreStoredScrollPosition()
                ? this.readStoredScrollPosition()
                : undefined;

          if (!Number.isFinite(scrollLeft)) {
            this.scrollSelectedSwatchIntoView();
            this.persistScrollPosition();
            return;
          }

          const maxScrollLeft = Math.max(this.strip.scrollWidth - this.strip.clientWidth, 0);
          this.strip.scrollLeft = Math.min(Math.max(scrollLeft, 0), maxScrollLeft);
          this.persistScrollPosition();
          this.clearPendingInteractionScroll();
        }

        scrollSelectedSwatchIntoView() {
          const selectedInput = this.strip.querySelector('input[type="radio"]:checked');
          const selectedSwatch = selectedInput?.nextElementSibling?.classList?.contains('bpd-color-swatch')
            ? selectedInput.nextElementSibling
            : selectedInput;
          if (!selectedSwatch) return;

          const maxScrollLeft = Math.max(this.strip.scrollWidth - this.strip.clientWidth, 0);
          const targetScrollLeft =
            selectedSwatch.offsetLeft - (this.strip.clientWidth - selectedSwatch.offsetWidth) / 2;

          this.strip.scrollLeft = Math.min(Math.max(targetScrollLeft, 0), maxScrollLeft);
        }

        update() {
          if (!this.strip || !this.previousButton || !this.nextButton) return;

          const maxScrollLeft = Math.max(this.strip.scrollWidth - this.strip.clientWidth, 0);
          const hasPrevious = this.strip.scrollLeft > 2;
          const hasMore = maxScrollLeft - this.strip.scrollLeft > 2;

          this.classList.toggle('bpd-color-swatch-scroll--has-overflow', maxScrollLeft > 2);
          this.classList.toggle('bpd-color-swatch-scroll--has-previous', hasPrevious);
          this.classList.toggle('bpd-color-swatch-scroll--has-more', hasMore);
          this.previousButton.hidden = !hasPrevious;
          this.nextButton.hidden = !hasMore;
        }

        getStorageKey() {
          return `bpd-color-swatch-scroll:${window.location.pathname}`;
        }

        shouldRestoreStoredScrollPosition() {
          const navigationType = this.getNavigationType();
          return navigationType === 'reload' || navigationType === 'back_forward';
        }

        getNavigationType() {
          const entry = performance.getEntriesByType?.('navigation')?.[0];
          if (entry?.type) return entry.type;
          if (performance.navigation?.type === 1) return 'reload';
          if (performance.navigation?.type === 2) return 'back_forward';
          return 'navigate';
        }

        hasPendingInteractionScroll() {
          return Number.isFinite(this.readPendingInteractionScrollLeft());
        }

        readPendingInteractionScrollLeft() {
          if (!Number.isFinite(window.BpdColorSwatchPendingScrollLeft)) return undefined;
          if (!Number.isFinite(window.BpdColorSwatchPendingScrollAt)) return undefined;
          if (Date.now() - window.BpdColorSwatchPendingScrollAt > 3000) {
            this.clearPendingInteractionScroll();
            return undefined;
          }

          return window.BpdColorSwatchPendingScrollLeft;
        }

        clearPendingInteractionScroll() {
          window.BpdColorSwatchPendingScrollLeft = undefined;
          window.BpdColorSwatchPendingScrollAt = undefined;
        }

        readStoredScrollPosition() {
          try {
            const storedValue = sessionStorage.getItem(this.getStorageKey());
            if (storedValue === null) return undefined;

            const scrollLeft = Number(storedValue);
            return Number.isFinite(scrollLeft) ? scrollLeft : undefined;
          } catch (_error) {
            return undefined;
          }
        }

        persistScrollPosition() {
          if (!this.strip) return;

          window.BpdColorSwatchScrollLeft = this.strip.scrollLeft;
          try {
            sessionStorage.setItem(this.getStorageKey(), String(this.strip.scrollLeft));
          } catch (_error) {
            // Session storage can be disabled; the in-memory fallback above still handles same-render updates.
          }
        }
      },
    );
  }

  function bpdEnhanceColorSwatchStrips(root = document) {
    const strips = root.matches?.('.bpd-color-swatch-strip')
      ? [root]
      : Array.from(root.querySelectorAll?.('.bpd-color-swatch-strip') || []);

    strips.forEach((strip) => {
      if (strip.closest('bpd-color-swatch-scroll')) return;
      if (!strip.parentNode) return;

      const wrapper = document.createElement('bpd-color-swatch-scroll');
      wrapper.className = 'bpd-color-swatch-scroll';

      const previousButton = document.createElement('button');
      previousButton.type = 'button';
      previousButton.className = 'bpd-color-swatch-arrow bpd-color-swatch-arrow--previous';
      previousButton.setAttribute('aria-label', 'Show previous colors');
      previousButton.setAttribute('data-bpd-color-swatch-previous', '');
      previousButton.hidden = true;
      previousButton.innerHTML = '<span class=\"bpd-color-swatch-arrow__chevron\" aria-hidden=\"true\"></span>';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bpd-color-swatch-arrow bpd-color-swatch-arrow--next';
      button.setAttribute('aria-label', 'Show more colors');
      button.setAttribute('data-bpd-color-swatch-next', '');
      button.hidden = true;
      button.innerHTML = '<span class=\"bpd-color-swatch-arrow__chevron\" aria-hidden=\"true\"></span>';

      strip.parentNode.insertBefore(wrapper, strip);
      wrapper.appendChild(previousButton);
      wrapper.appendChild(strip);
      wrapper.appendChild(button);
      wrapper.initialize?.();
    });
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      const strip = event.target?.closest?.('.bpd-color-swatch-strip');
      if (strip) {
        const isSwatchPress =
          event.target?.closest?.('.bpd-color-swatch') || event.target?.matches?.('.bpd-color-swatch-input');
        window.BpdColorSwatchScrollLeft = strip.scrollLeft;
        if (isSwatchPress) {
          window.BpdColorSwatchPendingScrollLeft = strip.scrollLeft;
          window.BpdColorSwatchPendingScrollAt = Date.now();
        }
        try {
          sessionStorage.setItem(`bpd-color-swatch-scroll:${window.location.pathname}`, String(strip.scrollLeft));
        } catch (_error) {
          // Session storage can be disabled; keep the in-memory fallback above.
        }
      }
    },
    true,
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bpdEnhanceColorSwatchStrips(), { once: true });
  } else {
    bpdEnhanceColorSwatchStrips();
  }

  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) bpdEnhanceColorSwatchStrips(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
}
